import { useEffect, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';

import type { SituacaoDaConsulta } from './consultaNoCadastro';

/** Tempo minimo de cada etapa na tela. A barra e visual: o resultado vem do servidor. */
export const TEMPO_DA_ETAPA_MS = 800;

/** O que a seguradora ve conferir. */
const etapasDaSeguradora = [
  'Cadastro',
  'Listas',
  'Score',
  'Divida ativa',
  'Restritivos',
  'Limite',
];

/** A corretora ve so o andamento: nada de score nem de restricao, nem como nome de etapa. */
const etapasDaCorretora = [
  'Cadastro',
  'Listas',
  'Analise financeira',
  'Dividas',
  'Conferencia final',
  'Limite',
];

/**
 * Janela da consulta no cadastro do tomador (ADR 0102).
 *
 * A barra anda sozinha, com tempo minimo em cada etapa, para as etapas aparecerem, e para
 * na ultima enquanto o servidor nao responder. O resultado so aparece depois da resposta
 * real: aprovado nunca vem antes dela. Fechar a janela nao para nada: a analise continua
 * no servidor e fica registrada.
 */
export function JanelaDaConsulta({
  aberta,
  situacao,
  daCorretora,
  onFechar,
}: {
  aberta: boolean;
  situacao: SituacaoDaConsulta;
  daCorretora: boolean;
  onFechar: () => void;
}) {
  if (!aberta) return null;
  return (
    <ConteudoDaJanela
      situacao={situacao}
      etapas={daCorretora ? etapasDaCorretora : etapasDaSeguradora}
      onFechar={onFechar}
    />
  );
}

function ConteudoDaJanela({
  situacao,
  etapas,
  onFechar,
}: {
  situacao: SituacaoDaConsulta;
  etapas: string[];
  onFechar: () => void;
}) {
  const ultima = etapas.length - 1;
  const [etapa, setEtapa] = useState(0);

  useEffect(() => {
    if (etapa >= ultima) return undefined;
    const espera = setTimeout(() => setEtapa((atual) => atual + 1), TEMPO_DA_ETAPA_MS);
    return () => clearTimeout(espera);
  }, [etapa, ultima]);

  // A falha nao espera a barra: nao ha o que conferir. O resultado espera as etapas.
  const respondeu =
    situacao.fase === 'erro' || (situacao.fase !== 'analisando' && etapa >= ultima);
  const percentual = respondeu
    ? 100
    : Math.round((Math.min(etapa, ultima) / etapas.length) * 100) + 8;

  return (
    <Modal
      open
      onClose={onFechar}
      title="Consulta do tomador"
      description="As etapas sao conferidas em sequencia."
      size="sm"
      footer={
        <Button variant={respondeu ? 'primary' : 'secondary'} onClick={onFechar}>
          {respondeu ? 'Concluir' : 'Fechar'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          role="progressbar"
          aria-label="Andamento da consulta"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percentual}
          className="h-2 w-full overflow-hidden rounded-full bg-ink-100"
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-(--duration-base)',
              situacao.fase === 'erro' ? 'bg-danger-500' : 'bg-brand-500',
            )}
            style={{ width: `${percentual}%` }}
          />
        </div>

        <ol aria-label="Etapas da consulta" className="flex flex-col gap-2">
          {etapas.map((nome, indice) => (
            <EtapaDaConsulta
              key={nome}
              nome={nome}
              estado={
                respondeu && situacao.fase !== 'erro'
                  ? 'conferida'
                  : indice < etapa
                    ? 'conferida'
                    : indice === etapa
                      ? situacao.fase === 'erro'
                        ? 'pendente'
                        : 'conferindo'
                      : 'pendente'
              }
            />
          ))}
        </ol>

        {respondeu ? <ResultadoDaConsulta situacao={situacao} /> : <AvisoDeEspera />}
      </div>
    </Modal>
  );
}

function EtapaDaConsulta({
  nome,
  estado,
}: {
  nome: string;
  estado: 'conferida' | 'conferindo' | 'pendente';
}) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        aria-hidden
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full ring-1',
          estado === 'conferida' && 'bg-brand-500 text-white ring-brand-500',
          estado === 'conferindo' && 'animate-pulse bg-brand-100 ring-brand-500',
          estado === 'pendente' && 'bg-surface ring-line',
        )}
      >
        {estado === 'conferida' ? <Icon name="check" size={12} /> : null}
      </span>
      <span className={estado === 'pendente' ? 'text-ink-500' : 'text-ink-900'}>
        {nome}
      </span>
      <span className="sr-only">
        {estado === 'conferida'
          ? 'conferida'
          : estado === 'conferindo'
            ? 'em conferencia'
            : 'aguardando'}
      </span>
    </li>
  );
}

function AvisoDeEspera() {
  return (
    <p className="text-xs text-ink-600">
      Pode levar mais de um minuto. Se fechar esta janela, a analise continua e fica
      registrada no tomador.
    </p>
  );
}

function ResultadoDaConsulta({ situacao }: { situacao: SituacaoDaConsulta }) {
  switch (situacao.fase) {
    case 'aprovado':
      return (
        <Alert tone="success" title="Aprovado">
          {situacao.limite
            ? `Aprovado, limite de ${formatMoney(situacao.limite)}.`
            : 'Credito aprovado.'}
        </Alert>
      );
    case 'manual':
      return (
        <Alert tone="info" title="Foi para analise manual">
          Uma pessoa vai analisar este tomador.
        </Alert>
      );
    case 'erro':
      return (
        <Alert tone="danger" title="Nao foi possivel concluir a consulta">
          {situacao.mensagem}
        </Alert>
      );
    default:
      return null;
  }
}
